/**
 * Pending Customers Page
 * Manage customers who have earned points but haven't signed up yet
 *
 * Features:
 * - View all pending accounts (phone number only)
 * - See accumulated pending points
 * - Send SMS invitations to claim points
 * - Track conversion from pending to active
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, toast } from '../components/ui';
import api from '../lib/api';

interface PendingCustomer {
  id: string;
  phoneNumber: string;
  pendingStars: string;
  transactionCount: number;
  firstTransactionAt: string;
  lastTransactionAt: string;
  totalSpent: string;
  invitationsSent: number;
  lastInvitationSent?: string;
  claimCode: string;
  status: string;
}

interface PendingStats {
  totalPending: number;
  totalPendingStars: number;
  avgPendingStars: number;
  highValuePending: number;
}

export const PendingCustomersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'pendingStars' | 'transactionCount' | 'lastTransactionAt'>('pendingStars');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [pendingCustomers, setPendingCustomers] = useState<PendingCustomer[]>([]);
  const [stats, setStats] = useState<PendingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);

  useEffect(() => {
    loadPendingCustomers();
    loadStats();
  }, [sortBy, order]);

  const loadPendingCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/pending-customers', {
        params: {
          sortBy,
          order,
          limit: 100,
          offset: 0,
        },
      });
      setPendingCustomers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load pending customers:', error);
      setPendingCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/api/v1/pending-customers/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSendInvitation = async (customerId: string) => {
    try {
      setSendingInvite(customerId);
      await api.post(`/api/v1/pending-customers/${customerId}/invite`);
      await loadPendingCustomers();
      toast.success('Invitation sent successfully!');
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error('Failed to send invitation. Please try again.');
    } finally {
      setSendingInvite(null);
    }
  };

  const handleBulkInvite = async () => {
    const confirmed = window.confirm(
      `Send invitations to ${stats?.highValuePending || 0} high-value pending customers (500+ Stars)?`
    );

    if (!confirmed) return;

    try {
      const response = await api.post('/api/v1/pending-customers/bulk-invite', {
        minPendingStars: 500,
      });
      toast.success(`Sent ${response.data.sent} invitations successfully!`);
      await loadPendingCustomers();
    } catch (error) {
      console.error('Failed to send bulk invitations:', error);
      toast.error('Failed to send bulk invitations. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const areaCode = cleaned.slice(1, 4);
      const prefix = cleaned.slice(4, 7);
      const line = cleaned.slice(7, 11);
      return `(${areaCode}) ${prefix}-${line}`;
    }
    return phone;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-orange-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-base text-black-lighter">Loading pending customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">Pending Customers</h1>
          <p className="text-base mt-2 text-black-lighter">
            Customers earning points before signing up
          </p>
        </div>
        <Button variant="primary" size="md" onClick={handleBulkInvite}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          Send Invitations to All
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-linear-to-r from-blue-50 to-blue-100 border-blue-200">
        <div className="p-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-blue-900 mb-2">How Pending Accounts Work</h3>
              <div className="text-sm text-blue-800 space-y-2">
                <p>
                  1. <strong>Customer Provides Phone Number:</strong> At checkout, staff asks for phone number and enters it in Square POS
                </p>
                <p>
                  2. <strong>Points Accumulate as Pending:</strong> Each transaction earns Stars tokens that are held as "pending" until they sign up
                </p>
                <p>
                  3. <strong>Automatic SMS Invitations:</strong> After 3 transactions or 500 pending Stars, we send an SMS invitation automatically
                </p>
                <p>
                  4. <strong>Instant Claim:</strong> When customer signs up with their phone number, all pending points are credited immediately
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-black-lighter">Total Pending</p>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-h2 font-bold text-black">{stats.totalPending}</p>
              <p className="text-xs text-black-lighter mt-1">Customers with pending points</p>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-black-lighter">Pending Stars</p>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-h2 font-bold text-black">
                {stats.totalPendingStars.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-xs text-black-lighter mt-1">Total unclaimed tokens</p>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-black-lighter">Avg. per Customer</p>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <p className="text-h2 font-bold text-black">
                {stats.avgPendingStars.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-xs text-black-lighter mt-1">Stars per pending customer</p>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-black-lighter">High Value</p>
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
              </div>
              <p className="text-h2 font-bold text-black">{stats.highValuePending}</p>
              <p className="text-xs text-black-lighter mt-1">With 500+ Stars pending</p>
            </div>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <div className="p-5">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Search by phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Input
              type="select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="sm:min-w-50"
            >
              <option value="pendingStars">Sort by Stars Pending</option>
              <option value="transactionCount">Sort by Transactions</option>
              <option value="lastTransactionAt">Sort by Last Activity</option>
            </Input>
            <Input
              type="select"
              value={order}
              onChange={(e) => setOrder(e.target.value as typeof order)}
              className="sm:min-w-37.5"
            >
              <option value="desc">High to Low</option>
              <option value="asc">Low to High</option>
            </Input>
          </div>
        </div>
      </Card>

      {/* Pending Customers Table */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h4 font-semibold text-black">Pending Customers</h3>
          <p className="text-sm mt-1 text-black-lighter">
            Send invitations to encourage sign-ups and claim points
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-border-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Pending Stars
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Transactions
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Last Visit
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Invitations
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-black-lighter uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border-light">
              {pendingCustomers.map((customer) => {
                const pendingStars = parseFloat(customer.pendingStars);
                const totalSpent = parseFloat(customer.totalSpent);

                return (
                  <tr key={customer.id} className="hover:bg-cream-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-base font-medium text-black">{formatPhone(customer.phoneNumber)}</p>
                        <p className="text-xs text-black-lighter">
                          First transaction {formatDate(customer.firstTransactionAt)}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-orange-400">
                          {pendingStars.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        {pendingStars > 500 && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded">
                            High Value
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-base text-black">{customer.transactionCount}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-base font-medium text-black">
                        ${totalSpent.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-black-lighter">
                      {formatDate(customer.lastTransactionAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.invitationsSent > 0 ? (
                        <div>
                          <p className="text-sm text-black">{customer.invitationsSent} sent</p>
                          {customer.lastInvitationSent && (
                            <p className="text-xs text-black-lighter">
                              Last: {formatDate(customer.lastInvitationSent)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-black-lighter">None yet</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSendInvitation(customer.id)}
                        disabled={sendingInvite === customer.id || customer.invitationsSent >= 5}
                      >
                        {sendingInvite === customer.id ? (
                          <>
                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                            {customer.invitationsSent >= 5 ? 'Max Sent' : 'Send SMS'}
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* SMS Template Preview */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h4 font-semibold text-black">SMS Invitation Template</h3>
          <p className="text-sm mt-1 text-black-lighter">
            Customize the message sent to pending customers
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Template Editor */}
            <div>
              <label className="block text-sm font-semibold text-black mb-2">
                Message Template
              </label>
              <textarea
                rows={6}
                defaultValue={`Hey! You've earned {{pending_b3tr}} Stars tokens at {{business_name}}! 🎉

Download the Buffables app to claim your rewards and start redeeming:
{{app_link}}

Your phone: {{phone}}`}
                className="w-full px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black font-mono focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <p className="text-xs text-black-lighter mt-2">
                Available variables: {'{'}{'{'} pending_b3tr {'}'}{'}'},  {'{'}{'{'} business_name {'}'}{'}'},  {'{'}{'{'} phone {'}'}{'}'},  {'{'}{'{'} app_link {'}'}{'}'}
              </p>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-semibold text-black mb-2">
                Preview
              </label>
              <div className="bg-gray-100 rounded-lg p-4 border-2 border-gray-300">
                <div className="bg-white rounded-2xl p-4 shadow-sm max-w-xs">
                  <p className="text-sm text-gray-800">
                    Hey! You've earned <strong>450 Stars tokens</strong> at <strong>Cozy Coffee Co.</strong>! 🎉
                  </p>
                  <p className="text-sm text-gray-800 mt-2">
                    Download the Buffables app to claim your rewards and start redeeming:
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    https://loyalty.app/claim/abc123
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Your phone: +1 (555) 123-4567
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Standard messaging rates apply
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Button variant="primary" size="md">
              Save Template
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PendingCustomersPage;
